/** XeAgents.ai**/
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * One numbered step of the form.
 *
 * The number is the whole point: four sections that look alike are a wall, and
 * the same four numbered read as a sequence with an end. It also gives the page
 * one place to change card padding and border weight, rather than four that
 * drift apart.
 */
export function StepCard({
  step,
  title,
  description,
  action,
  children,
  className,
}: {
  step: number;
  title: string;
  description: string;
  /** A single control that belongs to the whole step, e.g. "Use AI Suggested Instructions". */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("border-border/70 shadow-sm", className)} data-testid={`step-card-${step}`}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
              aria-hidden="true"
            >
              {step}
            </span>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold">{title}</CardTitle>
              <CardDescription className="mt-1 text-sm">{description}</CardDescription>
            </div>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

/**
 * A label, its control and the two things that go under it: the explanation, and
 * the error that replaces it.
 *
 * Inline, next to the field it is about - a summary at the top of the page makes
 * the reader hunt for which of eleven inputs it means.
 */
export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label
        htmlFor={htmlFor}
        className="flex items-center gap-1 text-sm font-medium text-foreground"
      >
        {label}
        {required && <span className="text-destructive" aria-hidden="true">*</span>}
        {required && <span className="sr-only">(required)</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert" data-testid={`error-${htmlFor}`}>
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground leading-snug">{hint}</p>
      ) : null}
    </div>
  );
}
